import { MenuProps, Spin } from 'antd';
import { DownloadKeys, DownloadLoader } from 'enums/GraphDropdownEnums';
import React, { FC, useRef, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Label,
  Tooltip,
  TooltipProps,
  Cell,
} from 'recharts';
import GraphDropDown from 'views/Dashboard/COBarChart/GraphDropdown';
import { BarProps, customPositionX } from '../constants';
import {
  CompanyDashboardProps,
  customFontTick,
  customMarginBar,
  customPosition,
  customStyleAnchor,
} from '../constants';
import styles from './barchart.module.scss';
import { downloadGraph } from 'shared/utils/downloadStaticGraph';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { LocationTableFields } from 'enums/CompanySetupEnums';
import CustomTooltip from 'shared/components/CustomisedTooltip';
import { formatValue } from 'shared/utils/formatWholeNum';
import { CustomizedTick } from './customizedTickUtils';

const MyBarChart: FC<BarProps> = ({
  data,
  fetchEmissionByLocation,
  orgId,
  locationId,
  year,
  XAxisLabel,
  loading,
}) => {
  const graphRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const handleDownload: MenuProps['onClick'] = async ({ key }) => {
    await downloadGraph(
      key,
      graphRef,
      CompanyDashboardProps.LOCATIONCHART,
      setIsDownloading
    );
    if (key === DownloadKeys.CSV) {
      if (
        locationId !== CompanyDashboardProps.ALL &&
        locationId !== undefined
      ) {
        fetchEmissionByLocation({
          year,
          organizationId: orgId,
          locationId,
          page: metaData?.currentPage,
          sortKey: LocationTableFields.DESC,
          exportType: DownloadKeys.CSV,
        });
      } else {
        fetchEmissionByLocation({
          year,
          organizationId: orgId,
          locationId: undefined,
          page: metaData?.currentPage,
          sortKey: LocationTableFields.DESC,
          exportType: DownloadKeys.CSV,
        });
      }
    }
  };
  const handleBarSort: MenuProps['onClick'] = ({ key }) => {
    const order =
      key === CompanyDashboardProps.TRUE
        ? LocationTableFields.ASC
        : LocationTableFields.DESC;
    fetchEmissionByLocation({
      year,
      organizationId: orgId,
      page: metaData?.currentPage,
      sortKey: order,
    });
  };
  const renderTooltipContent = (tooltipProps: TooltipProps<string, string>) => {
    const { payload } = tooltipProps;
    return payload && payload.length ? (
      <CustomTooltip
        isBar
        payload={payload[0]?.payload}
        unit={CompanyDashboardProps.TCO2}
      />
    ) : null;
  };
  const handlePageBack = async () => {
    if (metaData?.currentPage) {
      await fetchEmissionByLocation({
        year,
        organizationId: orgId,
        page: metaData?.currentPage - 1,
      });
    }
  };
  const handleNextPage = async () => {
    if (metaData?.currentPage) {
      await fetchEmissionByLocation({
        year,
        organizationId: orgId,
        page: metaData?.currentPage + 1,
      });
    }
  };

  const customPadding = {
    left: data?.locationCo2?.length === 1 ? 90 : 10,
    right: data?.locationCo2?.length === 1 ? 400 : 0,
  };
  const formatLabel = (value: number) => formatValue(value);
  const isPercentageZero = data?.locationCo2?.length !== 0;
  const metaData = data?.meta;
  const formatTick = (value: number) => {
    return String(Math.round(value));
  };

  return (
    <>
      {isPercentageZero ? (
        <Spin
          spinning={isDownloading || !!loading}
          {...(isDownloading && { tip: DownloadLoader.DOWNLOADING })}>
          <div className={styles.wrapper}>
            <div className={styles.barArea}>
              <div className={styles.positionedDiv}>
                <GraphDropDown
                  handleDownload={handleDownload}
                  isMainDashboard
                  showSort
                  handleSort={handleBarSort}
                  graphRef={graphRef}
                  isSort
                  setIsDownloading={setIsDownloading}
                />
              </div>
              <div ref={graphRef} className={styles.graphContent}>
                {metaData?.currentPage && metaData?.currentPage > 1 ? (
                  <div onClick={handlePageBack} className={styles.arrow}>
                    <LeftOutlined />
                  </div>
                ) : null}
                <ResponsiveContainer width={'100%'} height={400}>
                  <BarChart data={data?.locationCo2} margin={customMarginBar}>
                    <XAxis
                      dataKey="locationName"
                      strokeDasharray="5 5"
                      angle={-75}
                      dy={30}
                      dx={-39}
                      tick={
                        <CustomizedTick
                          x={20}
                          y={0}
                          payload={{
                            value: '',
                          }}
                        />
                      }
                      padding={customPadding}>
                      <Label
                        content={XAxisLabel}
                        position={customPositionX}
                        offset={-44}
                        style={customStyleAnchor}
                      />
                    </XAxis>
                    <Tooltip cursor={false} content={renderTooltipContent} />
                    <YAxis
                      domain={[0, 100]}
                      tick={customFontTick}
                      axisLine={false}
                      tickFormatter={formatTick}
                      tickLine={false}>
                      <Label
                        value={
                          CompanyDashboardProps.LABEL_VALUE +
                          `(${CompanyDashboardProps.TCO2})`
                        }
                        position={customPosition}
                        angle={-90}
                        offset={-10}
                        style={customStyleAnchor}
                      />
                    </YAxis>

                    <CartesianGrid
                      strokeDasharray="5 5"
                      horizontal={true}
                      vertical={false}
                    />

                    <Bar
                      dataKey={CompanyDashboardProps.BAR_DATAKEY}
                      radius={[5, 5, 0, 0]}
                      barSize={20}>
                      {data?.locationCo2?.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            entry.entityType === CompanyDashboardProps.LOC
                              ? CompanyDashboardProps.LOCATION_BAR_FILL
                              : CompanyDashboardProps.BAR_FILL
                          }
                        />
                      ))}
                      <LabelList
                        dataKey="totalCO2"
                        position="top"
                        formatter={formatLabel}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                {metaData?.currentPage &&
                metaData?.totalPages &&
                metaData.totalPages > metaData.currentPage ? (
                  <div onClick={handleNextPage} className={styles.arrow}>
                    <RightOutlined />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </Spin>
      ) : (
        <p className={styles.emptyState}>{CompanyDashboardProps.DATA_EMPTY}</p>
      )}
    </>
  );
};

export default MyBarChart;
